/**
 * @module BN3THSearch
 * @description Factory that creates independent predictive search instances.
 * Shared logic (fetch, render) lives in the outer closure. Per-instance
 * state (open/close, current query, debounce timer) lives in each create() call.
 *
 * Usage:
 *   BN3THSearch.create().init();                          // header overlay (defaults)
 *   BN3THSearch.create({ externalPanel: true })            // mobile slide-in panel
 *     .init({ overlay: "[data-js-mobile-search-overlay]", ... });
 *
 * With externalPanel: true, this instance assumes its overlay element's
 * open/close/Escape/outside-click is owned externally (a <bn3th-slide-panel>
 * wrapper, see assets/bn3th-slide-panel.js): open()/close() skip toggling
 * data-open/aria-hidden on el.overlay and skip binding their own document
 * Escape/outside-click listeners, but still handle the search-specific
 * side effects (focus the input, reset state). Callers call open()/close()
 * as usual, in response to the wrapper's own slidepanel:open/
 * slidepanel:close events (see assets/bn3th-mobile-menu.js).
 */
const BN3THSearch = (() => {
  const LOGGING = true;
  const MODULE_NAME = "bn3th-search.js";
  const SECTION_ID = "bn3th-ajax-predictive-search";

  const renderEmpty = (query) => `
    <div class="bn3th-search-empty">
      <p class="bn3th-search-empty__title">No results found</p>
      <p class="bn3th-search-empty__message">We couldn't find anything matching "${query}".</p>
    </div>
  `;

  /**
   * Creates a new search instance.
   *
   * @param {object} config
   * @param {number} [config.minChars=2]       Minimum characters before fetching.
   * @param {number} [config.debounceMs=300]   Debounce delay in milliseconds.
   * @param {number} [config.resultsLimit=10]  Max results requested per resource type (products, articles, pages).
   *                                            Articles and pages are further capped to 4 each in the predictive
   *                                            search section template; products are shown up to this limit.
   * @param {string} [config.dismissArea]      Selector for the area that should NOT close search on click.
   */
  const create = (config = {}) => {
    const settings = {
      minChars: 2,
      debounceMs: 300,
      resultsLimit: 10,
      dismissArea: ".bn3th-site-header",
      externalPanel: false,
      ...config,
    };

    let el = {};
    let debounceTimer = null;
    let currentQuery = "";

    const open = () => {
      if (!settings.externalPanel) {
        el.overlay.setAttribute("data-open", "");
        el.overlay.setAttribute("aria-hidden", "false");
        document.addEventListener("keydown", onKeydown);
        document.addEventListener("click", onDocumentClick, true);
      }
      setTimeout(() => el.input.focus(), 0);
      LOGGING && console.log(`[${MODULE_NAME}] :: open()`);
    };

    const close = () => {
      if (!settings.externalPanel) {
        el.overlay.removeAttribute("data-open");
        el.overlay.setAttribute("aria-hidden", "true");
        document.removeEventListener("keydown", onKeydown);
        document.removeEventListener("click", onDocumentClick, true);
      }
      el.panel.removeAttribute("data-open");
      el.input.value = "";
      currentQuery = "";
      LOGGING && console.log(`[${MODULE_NAME}] :: close()`);
    };

    const closePanel = () => {
      el.panel.removeAttribute("data-open");
      el.results.innerHTML = "";
    };

    const onKeydown = (e) => {
      if (e.key === "Escape") close();
    };

    const onDocumentClick = (e) => {
      if (!e.target.closest(settings.dismissArea)) close();
    };

    const onInput = (e) => {
      const query = e.target.value.trim();
      clearTimeout(debounceTimer);

      if (query.length < settings.minChars) {
        closePanel();
        return;
      }

      debounceTimer = setTimeout(() => fetchResults(query), settings.debounceMs);
    };

    const fetchResults = async (query) => {
      if (query === currentQuery) return;
      currentQuery = query;

      const params = new URLSearchParams({
        q: query,
        "resources[type]": "product,article,page,collection",
        "resources[limit]": settings.resultsLimit,
        "resources[limit_scope]": "each",
        "resources[options][unavailable_products]": "last",
        section_id: SECTION_ID,
      });

      try {
        const response = await fetch(`/search/suggest?${params}`);
        const html = await response.text();
        render(html, query);
      } catch (err) {
        LOGGING && console.warn(`[${MODULE_NAME}] :: fetchResults() :: Fetch failed`, err);
      }
    };

    const render = (html, query) => {
      const markup = new DOMParser().parseFromString(html, "text/html").querySelector(`#shopify-section-${SECTION_ID}`)?.innerHTML ?? "";

      el.results.innerHTML = markup.trim() ? markup : renderEmpty(query);
      el.panel.setAttribute("data-open", "");

      LOGGING && console.log(`[${MODULE_NAME}] :: render() :: hasResults: ${Boolean(markup.trim())}`);
    };

    /**
     * Binds the instance to the DOM. Accepts optional selector overrides for
     * non-standard contexts (e.g. mobile drawer).
     *
     * @param {object} [selectors]
     */
    const init = (selectors = {}) => {
      LOGGING && console.log(`[${MODULE_NAME}] :: create().init() :: Start`);

      const s = {
        trigger: "[data-js-search-trigger]",
        overlay: "[data-js-search-overlay]",
        input: "[data-js-search-input]",
        panel: "[data-js-search-panel]",
        results: "[data-js-search-results]",
        close: "[data-js-search-close]",
        ...selectors,
      };

      el = {
        trigger: document.querySelector(s.trigger),
        overlay: document.querySelector(s.overlay),
        input: document.querySelector(s.input),
        panel: document.querySelector(s.panel),
        results: document.querySelector(s.results),
        close: document.querySelector(s.close),
      };

      if (!el.overlay || !el.input) {
        LOGGING && console.warn(`[${MODULE_NAME}] :: create().init() :: Required elements not found.`);
        return;
      }

      el.trigger?.addEventListener("click", (e) => {
        e.preventDefault();
        open();
      });

      el.close?.addEventListener("click", close);
      el.input.addEventListener("input", onInput);

      LOGGING && console.log(`[${MODULE_NAME}] :: create().init() :: Complete`);
    };

    return { init, open, close };
  };

  return { create };
})();
